import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { userRequest } from '../utils/requestMethods';
import { Header, BackButton, SkeletonTable, FormQuestion, QuestionSidebar } from '../components';
import { useStateContext } from '../contexts/ContextProvider';
import { FiCheck, FiX, FiPlay, FiPause, FiSave, FiFileText, FiTrash2, FiMoreVertical, FiInfo } from 'react-icons/fi';
import { MdPublish, MdAdd } from 'react-icons/md';
import { toast } from 'react-toastify';
import { exportQuestionsToExcel, importQuestionsFromFile } from '../utils/questionExportImport';
import NappsImportModal from '../components/teacherModule/onlineTest/NappsImportModal';
import PublishTestErrorModal, {
  parsePublishPointsError,
  getPublishErrorMessage,
} from '../components/PublishTestErrorModal';

// Mark Distribution Summary Component
const MarkDistributionSummary = ({ totalScore, questions, scoringMode, mcqMaxScore, theoryMaxScore }) => {
  const distribution = useMemo(() => {
    const questionsWithPoints = questions.filter(q => q.point !== undefined && q.point !== "" && q.point !== null);
    const questionsWithoutPoints = questions.filter(q => !q.point || q.point === "" || q.point === null);

    const assignedPoints = questionsWithPoints.reduce((sum, q) => sum + (parseFloat(q.point) || 0), 0);
    const remainingPoints = totalScore - assignedPoints;
    const unassignedCount = questionsWithoutPoints.length;
    const pointsPerUnassigned = unassignedCount > 0 && remainingPoints > 0 ? (remainingPoints / unassignedCount).toFixed(2) : 0;

    return {
      totalScore,
      assignedPoints,
      remainingPoints,
      unassignedCount,
      pointsPerUnassigned,
      questionsWithPoints,
      questionsWithoutPoints,
      scoringMode,
      mcqMaxScore,
      theoryMaxScore
    };
  }, [totalScore, questions, scoringMode, mcqMaxScore, theoryMaxScore]);

  return (
    <div className="mb-6 bg-gradient-to-r from-indigo-50 to-blue-50 border-2 border-indigo-200 rounded-2xl p-6 shadow-sm">
      <div className="flex items-start gap-3 mb-4">
        <div className="flex-shrink-0 mt-1">
          <FiInfo className="w-5 h-5 text-indigo-600" />
        </div>
        <div className="flex-1">
          <h3 className="text-lg font-semibold text-slate-800 mb-1">Mark Distribution Summary</h3>
          <p className="text-sm text-slate-600">See how marks are allocated across questions</p>
        </div>
      </div>

      <div className={`grid gap-4 mb-4 ${distribution.scoringMode === 'partial_mcq' ? 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6' : 'grid-cols-1 md:grid-cols-2 lg:grid-cols-4'}`}>
        <div className="bg-white rounded-xl p-4 border border-indigo-100">
          <div className="text-xs font-medium text-slate-600 mb-1">Total Test Marks</div>
          <div className="text-2xl font-bold text-indigo-700">{distribution.totalScore}</div>
        </div>
        {distribution.scoringMode === 'partial_mcq' && distribution.mcqMaxScore && (
          <div className="bg-white rounded-xl p-4 border border-blue-200">
            <div className="text-xs font-medium text-slate-600 mb-1">MCQ Max Marks</div>
            <div className="text-2xl font-bold text-blue-700">{distribution.mcqMaxScore}</div>
            <div className="text-xs text-blue-600 mt-1">Auto-graded</div>
          </div>
        )}
        {distribution.scoringMode === 'partial_mcq' && distribution.theoryMaxScore && (
          <div className="bg-white rounded-xl p-4 border border-purple-200">
            <div className="text-xs font-medium text-slate-600 mb-1">Theory Max Marks</div>
            <div className="text-2xl font-bold text-purple-700">{distribution.theoryMaxScore}</div>
            <div className="text-xs text-purple-600 mt-1">Manual grading</div>
          </div>
        )}
        <div className="bg-white rounded-xl p-4 border border-indigo-100">
          <div className="text-xs font-medium text-slate-600 mb-1">Assigned Marks</div>
          <div className="text-2xl font-bold text-emerald-700">{distribution.assignedPoints.toFixed(2)}</div>
          <div className="text-xs text-slate-500 mt-1">
            {distribution.questionsWithPoints.length} question{distribution.questionsWithPoints.length !== 1 ? 's' : ''}
          </div>
        </div>
        <div className="bg-white rounded-xl p-4 border border-indigo-100">
          <div className="text-xs font-medium text-slate-600 mb-1">Remaining Marks</div>
          <div className={`text-2xl font-bold ${distribution.remainingPoints < 0 ? 'text-red-700' : 'text-amber-700'}`}>
            {distribution.remainingPoints.toFixed(2)}
          </div>
        </div>
        <div className="bg-white rounded-xl p-4 border border-indigo-100">
          <div className="text-xs font-medium text-slate-600 mb-1">Per Unassigned Question</div>
          <div className="text-2xl font-bold text-blue-700">
            {distribution.unassignedCount > 0 && distribution.remainingPoints > 0 ? distribution.pointsPerUnassigned : '0.00'}
          </div>
          <div className="text-xs text-slate-500 mt-1">
            {distribution.unassignedCount} question{distribution.unassignedCount !== 1 ? 's' : ''}
          </div>
        </div>
      </div>

      {distribution.questionsWithPoints.length > 0 && (
        <div className="mt-4 bg-white rounded-xl p-4 border border-indigo-100">
          <div className="text-sm font-semibold text-slate-700 mb-2">Questions with Assigned Marks:</div>
          <div className="flex flex-wrap gap-2">
            {distribution.questionsWithPoints.map((q, idx) => (
              <div key={idx} className="inline-flex items-center gap-1 px-3 py-1 bg-emerald-100 text-emerald-800 rounded-lg text-xs font-medium">
                <span>Q{questions.findIndex(q2 => q2.id === q.id) + 1}:</span>
                <span className="font-bold">{q.point} mark{q.point !== 1 ? 's' : ''}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {distribution.remainingPoints < 0 && (
        <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
          <div className="flex items-center gap-2 text-red-700 text-sm font-medium">
            <FiX className="w-4 h-4" />
            <span>Warning: Assigned marks ({distribution.assignedPoints.toFixed(2)}) exceed total test marks ({distribution.totalScore})</span>
          </div>
        </div>
      )}
    </div>
  );
};

const ViewTestQuestions = () => {
  const { testId } = useParams();
  const history = useHistory();
  const { school } = useStateContext();
  const schoolId = school?._id;

  const [test, setTest] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  // Set when publishing is blocked because question marks don't add up
  const [publishPointsError, setPublishPointsError] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [deletingQuestionId, setDeletingQuestionId] = useState(null);
  const [fetchLoading, setFetchLoading] = useState(true);
  const [isPublished, setIsPublished] = useState(false);
  const [importLoading, setImportLoading] = useState(false);
  const [showNappsModal, setShowNappsModal] = useState(false);
  const [showMoreDropdown, setShowMoreDropdown] = useState(false);
  const fileInputRef = React.useRef(null);
  const moreDropdownRef = React.useRef(null);

  useEffect(() => {
    if (testId && schoolId) {
      fetchTestDetails();
      fetchQuestions();
    }
  }, [testId, schoolId]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (moreDropdownRef.current && !moreDropdownRef.current.contains(event.target)) {
        setShowMoreDropdown(false);
      }
    };

    if (showMoreDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showMoreDropdown]);

  const fetchTestDetails = async () => {
    try {
      const { data } = await userRequest.get(`/test/${testId}`);
      setTest(data);
      return data;
    } catch (error) {
      console.log('Error fetching test details:', error);
      setError('Failed to load test details');
      return null;
    }
  };

  const fetchQuestions = async () => {
    try {
      setFetchLoading(true);
      const { data } = await userRequest.get(`/question?school_id=${schoolId}&test_id=${testId}&limit=100`);
      
      // Check if the response indicates the test is published
      if (typeof data === 'string' && data.includes("Test is published")) {
        setIsPublished(true);
        setQuestions([]);
        return;
      }
      
      // If we successfully get questions, reset the published flag
      setIsPublished(false);
      
      const existingQuestions = data.questions || [];
      
      // Map existing questions to include temporary IDs for form management
      const formattedQuestions = existingQuestions.map(q => ({
        id: q._id || Date.now(),
        questionId: q._id, // Store the actual question ID for updates
        question: q.question,
        options: q.options || ["Option 1"],
        correct_answer: q.correct_answer,
        type: q.type || "multiple_choice",
        is_active: q.is_active !== undefined ? q.is_active : true,
        point: q.point !== undefined ? q.point : undefined
      }));

      // If no questions exist, add a default one
      if (formattedQuestions.length === 0) {
        formattedQuestions.push({
          id: Date.now(),
          question: "",
          options: ["Option 1"],
          correct_answer: "",
          type: "multiple_choice",
          is_active: true,
          point: undefined
        });
      }

      setQuestions(formattedQuestions);
    } catch (error) {
      console.log('Error fetching questions:', error);
      setError('Failed to load questions');
    } finally {
      setFetchLoading(false);
      setLoading(false);
    }
  };

  const handleStatusToggle = async () => {
    if (!test) return;
    
    setActionLoading(true);
    try {
      const newStatus = test.status === 'draft' ? 'published' : 'draft';
      // A shared test has no single class — test.class here is the class GROUP shown for
      // display purposes (enriched server-side), not a real class id, and must never be sent
      // back as `class` on an edit (that would silently overwrite class_group_id scoping).
      const isSharedTest = Boolean(test.class_group_id);
      const requestBody = {
        title: test.title,
        description: test.description,
        duration: test.duration,
        total_score: test.total_score,
        school: test.school._id,
        ...(isSharedTest ? {} : { class: test.class._id, subject: test.subject?._id }),
        is_active: test.is_active,
        term: test.term,
        session: test.session,
        type: test.type,
        ...(test.type === 'ca' && { ca: test.ca }),
        status: newStatus
      };

      await userRequest.put(`/test/${testId}`, requestBody);
      
      // Fetch updated test details
      const updatedTest = await fetchTestDetails();
      
      if (updatedTest) {
        setTest(updatedTest);
        toast.success(`Test ${updatedTest.status === 'published' ? 'published' : 'moved to draft'} successfully!`);
      }
      
      // Refresh questions after status change to check accessibility
      await fetchQuestions();
    } catch (error) {
      console.log('Error updating test status:', error);
      const pointsError = parsePublishPointsError(error);
      if (pointsError) {
        setPublishPointsError(pointsError);
      } else {
        toast.error(getPublishErrorMessage(error));
      }
    } finally {
      setActionLoading(false);
    }
  };

  const handleActiveToggle = async () => {
    if (!test) return;
    
    setActionLoading(true);
    try {
      const newActiveStatus = !test.is_active;
      const isSharedTest = Boolean(test.class_group_id);
      const requestBody = {
        title: test.title,
        description: test.description,
        duration: test.duration,
        total_score: test.total_score,
        school: test.school._id,
        ...(isSharedTest ? {} : { class: test.class._id, subject: test.subject?._id }),
        is_active: newActiveStatus,
        term: test.term,
        session: test.session,
        type: test.type,
        ...(test.type === 'ca' && { ca: test.ca }),
        status: test.status
      };

      await userRequest.put(`/test/${testId}`, requestBody);
      
      setTest(prev => ({ ...prev, is_active: newActiveStatus }));
      toast.success(`Test ${newActiveStatus ? 'activated' : 'deactivated'} successfully!`);
    } catch (error) {
      console.log('Error updating test status:', error);
      toast.error('Failed to update test status');
    } finally {
      setActionLoading(false);
    }
  };

  const addQuestion = () => {
    setQuestions([
      ...questions,
      { id: Date.now(), question: "", options: ["Option 1"], correct_answer: "", type: "multiple_choice", is_active: true, point: undefined }
    ]);
  };

  const updateQuestion = (id, updatedData) => {
    setQuestions(questions.map(q => (q.id === id ? { ...q, ...updatedData } : q)));
  };

  const deleteQuestion = async (id) => {
    const questionToDelete = questions.find(q => q.id === id);
    
    if (questionToDelete && questionToDelete.questionId) {
      // This is an existing question with a server ID, delete from backend
      setDeletingQuestionId(id);
      try {
        await userRequest.delete(`/question/${questionToDelete.questionId}?school_id=${schoolId}`);
        toast.success("Question deleted successfully!");
      } catch (error) {
        console.error("Error deleting question:", error);
        toast.error("Failed to delete question");
        setDeletingQuestionId(null);
        return; // Don't remove from local list if deletion failed
      } finally {
        setDeletingQuestionId(null);
      }
    }
    
    // Remove from local list (for both existing and new questions)
    setQuestions(questions.filter(q => q.id !== id));
  };

  const duplicateQuestion = (id) => {
    const questionToDuplicate = questions.find(q => q.id === id);
    if (!questionToDuplicate) return;

    // Create a duplicate with a new ID and NO questionId (making it a new question)
    const duplicatedQuestion = {
      id: Date.now(),
      question: questionToDuplicate.question,
      options: [...questionToDuplicate.options],
      correct_answer: questionToDuplicate.correct_answer,
      type: questionToDuplicate.type,
      is_active: questionToDuplicate.is_active,
      point: questionToDuplicate.point
      // Explicitly NO questionId - this will be treated as a completely new question
    };

    // Find the index of the original question and insert the duplicate after it
    const originalIndex = questions.findIndex(q => q.id === id);
    const newQuestions = [...questions];
    newQuestions.splice(originalIndex + 1, 0, duplicatedQuestion);
    
    setQuestions(newQuestions);
    toast.success("Question duplicated successfully!");
  };

  const toggleActiveQuestion = (id) => {
    const questionToToggle = questions.find(q => q.id === id);
    if (!questionToToggle) return;

    // Just update local state - no API call needed
    updateQuestion(id, { is_active: !questionToToggle.is_active });
  };

  const handleNappsImport = (importedQuestions) => {
    if (!importedQuestions?.length) return;
    setQuestions(prev => [...prev, ...importedQuestions]);
    toast.success(`Imported ${importedQuestions.length} question(s) from NABS bank!`);
  };

  const handleImportClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const validTypes = [
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // .xlsx
      'application/vnd.ms-excel', // .xls
      'text/csv', // .csv
      'application/csv'
    ];
    
    const fileExtension = file.name.split('.').pop()?.toLowerCase();
    const isValidFile = validTypes.includes(file.type) || 
                       ['xlsx', 'xls', 'csv'].includes(fileExtension);

    if (!isValidFile) {
      toast.error("Please upload a valid Excel (.xlsx, .xls) or CSV file");
      e.target.value = ''; // Reset file input
      return;
    }

    setImportLoading(true);
    try {
      const importedQuestions = await importQuestionsFromFile(file);
      
      if (importedQuestions && importedQuestions.length > 0) {
        // Add imported questions to existing questions
        setQuestions([...questions, ...importedQuestions]);
        toast.success(`Successfully imported ${importedQuestions.length} question(s)!`);
      } else {
        toast.error("No valid questions found in the file");
      }
    } catch (error) {
      console.error("Error importing questions:", error);
      toast.error(error.message || "Failed to import questions. Please check the file format.");
    } finally {
      setImportLoading(false);
      e.target.value = ''; // Reset file input
    }
  };

  const handleExportClick = () => {
    try {
      const filename = `${test.title || 'questions'}_export.xlsx`;
      const result = exportQuestionsToExcel(questions, filename);
      if (result?.truncated) {
        toast.warning("Questions exported, but some fields were too long and got truncated to fit Excel's limits.");
      } else {
        toast.success("Questions exported successfully!");
      }
    } catch (error) {
      console.error("Error exporting questions:", error);
      toast.error("Failed to export questions");
    }
  };

  const submitForm = async () => {
    try {
      // Validate mark distribution before submitting
      const questionsWithPoints = questions.filter(q => q.point !== undefined && q.point !== "" && q.point !== null);
      const assignedPoints = questionsWithPoints.reduce((sum, q) => sum + (parseFloat(q.point) || 0), 0);
      const totalScore = test.total_score || 0;
      
      if (assignedPoints > totalScore) {
        const errorMessage = `Cannot save: Assigned marks (${assignedPoints.toFixed(2)}) exceed total test marks (${totalScore}). Please adjust the point values.`;
        setError(errorMessage);
        toast.error(errorMessage);
        setTimeout(() => setError(""), 8000);
        return;
      }

      const questionsPayload = questions.map(({ id, ...q }) => {
        const questionData = {
          question: q.question,
          type: q.type,
          is_active: q.is_active
        };
        
        // Only include options for multiple_choice questions
        if (q.type === "multiple_choice") {
          questionData.options = q.options;
        }
        
        // Only include correct_answer if it's not empty (for text questions, don't send empty string)
        if (q.correct_answer && q.correct_answer.trim() !== "") {
          questionData.correct_answer = q.correct_answer;
        }
        
        // Include point if it's set. A cleared mark on an existing question is sent as null,
        // which tells the backend to remove it (leaving it out keeps the old mark).
        if (q.point !== undefined && q.point !== "" && q.point !== null) {
          questionData.point = parseFloat(q.point);
        } else if (q.questionId) {
          questionData.point = null;
        }
        
        // If question has an existing ID, include it for updates
        if (q.questionId) {
          questionData.questionId = q.questionId;
        }
        
        return questionData;
      });

      const payload = {
        questions: questionsPayload,
        school: test.school._id || test.school,
        subject: test.subject?._id || test.subject,
        // A shared test has no single class — test.class here is the class GROUP shown for
        // display purposes (enriched server-side), not a real class id.
        ...(test.class_group_id ? {} : { class: test.class._id || test.class }),
        test: test._id
      };

      //console.log("Updating questions:", payload);
      await userRequest.put("/question/multiple", payload);
      setSuccess(true);
      toast.success("Questions updated successfully!");
      
      // Refresh the questions list after successful update
      await fetchQuestions();
      
      setTimeout(() => {
        setSuccess(false);
      }, 2000);
    } catch (error) {
      console.error("Error updating questions:", error);
      setError("An error occurred while updating questions.");
      toast.error("Failed to update questions");
      setTimeout(() => setError(""), 5000);
    }
  };

  const submitHandler = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await submitForm();
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      draft: { bg: 'bg-slate-100', text: 'text-slate-700', label: 'Draft', icon: FiFileText },
      published: { bg: 'bg-emerald-100', text: 'text-emerald-700', label: 'Published', icon: FiCheck },
      completed: { bg: 'bg-blue-100', text: 'text-blue-700', label: 'Completed', icon: FiCheck },
      cancelled: { bg: 'bg-rose-100', text: 'text-rose-700', label: 'Cancelled', icon: FiX }
    };

    const config = statusConfig[status] || statusConfig.draft;
    const IconComponent = config.icon;
    
    return (
      <span className={`inline-flex items-center px-3 py-1.5 rounded-lg text-sm font-medium ${config.bg} ${config.text} border border-opacity-20`}>
        <IconComponent className="w-4 h-4 mr-2" />
        {config.label}
      </span>
    );
  };

  const getActiveBadge = (isActive) => {
    if (isActive) {
      return (
        <span className="inline-flex items-center px-3 py-1.5 rounded-lg text-sm font-medium bg-emerald-100 text-emerald-700 border border-emerald-200">
          <FiPlay className="w-4 h-4 mr-2" />
          Active
        </span>
      );
    }
    return (
      <span className="inline-flex items-center px-3 py-1.5 rounded-lg text-sm font-medium bg-slate-100 text-slate-700 border border-slate-200">
        <FiPause className="w-4 h-4 mr-2" />
        Inactive
      </span>
    );
  };

  const getTypeBadge = (type, ca) => {
    if (type === 'ca') {
      return (
        <span className="inline-flex items-center px-3 py-1.5 rounded-lg text-sm font-medium bg-violet-100 text-violet-700 border border-violet-200">
          CA {ca}
        </span>
      );
    }
    return (
      <span className="inline-flex items-center px-3 py-1.5 rounded-lg text-sm font-medium bg-amber-100 text-amber-700 border border-amber-200">
        {type?.toUpperCase() || 'EXAM'}
      </span>
    );
  };

  const getCategoryBadge = (category) => {
    const categoryConfig = {
      assessment: { bg: 'bg-blue-100', text: 'text-blue-700', border: 'border-blue-200', label: 'Assessment' },
      learning: { bg: 'bg-purple-100', text: 'text-purple-700', border: 'border-purple-200', label: 'Learning' }
    };
    const config = categoryConfig[category] || categoryConfig.assessment;
    return (
      <span className={`inline-flex items-center px-3 py-1.5 rounded-lg text-sm font-medium ${config.bg} ${config.text} border ${config.border}`}>
        {config.label}
      </span>
    );
  };

  const getScoringModeBadge = (scoringMode) => {
    if (!scoringMode || scoringMode === 'full') return null;
    return (
      <span className="inline-flex items-center px-3 py-1.5 rounded-lg text-sm font-medium bg-blue-100 text-blue-700 border border-blue-200">
        Partial Scoring
      </span>
    );
  };

  if (fetchLoading || loading) {
    return (
      <>
        <div className="px-10 pt-6">
          <BackButton history={history} />
        </div>
        <div className="px-10 pt-5">
          <Header category="CBT Exams" title="Loading..." />
        </div>
        <div className="m-2 md:m-10 mt-24 p-2 md:p-10 bg-white rounded-3xl dark:bg-secondary-dark-bg min-h-screen">
          <SkeletonTable rows={5} columns={4} />
        </div>
      </>
    );
  }

  if (error || !test) {
    return (
      <>
        <div className="px-10 pt-6">
          <BackButton history={history} />
        </div>
        <div className="px-10 pt-5">
          <Header category="CBT Exams" title="Error" />
        </div>
        <div className="m-2 md:m-10 mt-24 p-2 md:p-10 bg-white rounded-3xl dark:bg-secondary-dark-bg min-h-screen">
          <div className="text-center py-16">
            <div className="mx-auto h-24 w-24 text-slate-300 mb-6 flex items-center justify-center">
              <FiX className="w-16 h-16" />
            </div>
            <h3 className="text-lg font-semibold text-slate-700 mb-2">Failed to load test</h3>
            <p className="text-slate-500 text-sm">{error || 'Test not found'}</p>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <div className="px-10 pt-6">
        <BackButton history={history} />
      </div>
      
      <div className="px-10 pt-5">
        <Header category="CBT Exams" title="Test Questions" />
      </div>

      {/* Test Details Header */}
      <div className="m-2 md:m-10 mt-24 p-2 md:p-10 bg-white rounded-3xl dark:bg-secondary-dark-bg min-h-screen">
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-2xl p-8 mb-8">
          <div className="flex flex-col lg:flex-row justify-between items-start lg:items-start gap-6">
            <div className="flex-1 min-w-0 lg:max-w-[calc(100%-280px)]">
              <h1 className="text-2xl font-bold text-slate-800 mb-2">{test.title}</h1>
              {test.description && (
                <p className="text-slate-600 text-base mb-4 leading-relaxed">{test.description}</p>
              )}
              
              <div className="flex flex-wrap items-center gap-4 mb-6">
                {getCategoryBadge(test.category)}
                {getStatusBadge(test.status)}
                {getActiveBadge(test.is_active)}
                {getTypeBadge(test.type, test.ca)}
                {getScoringModeBadge(test.scoring_mode)}
              </div>

              {/* Test Information */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                <div className="bg-white/60 backdrop-blur-sm rounded-xl p-3 border border-white/20">
                  <div className="text-xs font-medium text-slate-600 mb-1">Subject</div>
                  <div className="text-sm font-semibold text-slate-800">{test.subject?.subject_name}</div>
                  <div className="text-xs text-slate-500 mt-1">{test.subject?.subject_code}</div>
                </div>
                <div className="bg-white/60 backdrop-blur-sm rounded-xl p-3 border border-white/20">
                  <div className="text-xs font-medium text-slate-600 mb-1">Class</div>
                  <div className="text-sm font-semibold text-slate-800">{test.class?.class_name}</div>
                  <div className="text-xs text-slate-500 mt-1">{test.class?.class_code}</div>
                </div>
                <div className="bg-white/60 backdrop-blur-sm rounded-xl p-3 border border-white/20">
                  <div className="text-xs font-medium text-slate-600 mb-1">Created By</div>
                  <div className="text-sm font-semibold text-slate-800">
                    {test.created_by?.firstname} {test.created_by?.lastname}
                  </div>
                  <div className="text-xs text-slate-500 mt-1">{test.created_by?.email}</div>
                </div>
              </div>

              <div className={`grid gap-4 ${test.scoring_mode === 'partial_mcq' ? 'grid-cols-2 md:grid-cols-3 lg:grid-cols-6' : 'grid-cols-2 md:grid-cols-4'}`}>
                <div className="bg-white/60 backdrop-blur-sm rounded-xl p-3 border border-white/20">
                  <div className="text-xs font-medium text-slate-600 mb-1">Total Score</div>
                  <div className="text-lg font-bold text-slate-800">{test.total_score}</div>
                </div>
                {test.scoring_mode === 'partial_mcq' && test.mcq_max_score && (
                  <div className="bg-white/60 backdrop-blur-sm rounded-xl p-3 border border-white/20">
                    <div className="text-xs font-medium text-slate-600 mb-1">MCQ Max Score</div>
                    <div className="text-lg font-bold text-blue-700">{test.mcq_max_score}</div>
                  </div>
                )}
                {test.scoring_mode === 'partial_mcq' && test.theory_max_score && (
                  <div className="bg-white/60 backdrop-blur-sm rounded-xl p-3 border border-white/20">
                    <div className="text-xs font-medium text-slate-600 mb-1">Theory Max Score</div>
                    <div className="text-lg font-bold text-purple-700">{test.theory_max_score}</div>
                  </div>
                )}
                <div className="bg-white/60 backdrop-blur-sm rounded-xl p-3 border border-white/20">
                  <div className="text-xs font-medium text-slate-600 mb-1">Duration</div>
                  <div className="text-lg font-bold text-slate-800">{test.duration} min</div>
                </div>
                <div className="bg-white/60 backdrop-blur-sm rounded-xl p-3 border border-white/20">
                  <div className="text-xs font-medium text-slate-600 mb-1">Questions</div>
                  <div className="text-lg font-bold text-slate-800">{questions.length}</div>
                </div>
                <div className="bg-white/60 backdrop-blur-sm rounded-xl p-3 border border-white/20">
                  <div className="text-xs font-medium text-slate-600 mb-1">Created</div>
                  <div className="text-xs font-semibold text-slate-800">
                    {new Date(test.createdAt).toLocaleDateString()}
                  </div>
                </div>
              </div>

              {/* Scoring Mode Info Banner */}
              {test.scoring_mode === 'partial_mcq' && (
                <div className="mt-4 bg-blue-50 border border-blue-200 rounded-xl p-4">
                  <div className="flex items-start gap-3">
                    <FiInfo className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <h4 className="text-sm font-semibold text-blue-900 mb-1">Partial Scoring Mode</h4>
                      <p className="text-xs text-blue-700">
                        This exam uses partial scoring: Which means the exam will be auto-graded to ({test.mcq_max_score || 0} marks) immediately it is completed by the students,
                        while the remaining ({test.theory_max_score || 0} marks) can be graded manually by teachers later after the exam is completed.
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="flex flex-col sm:flex-row gap-3 flex-shrink-0 w-full sm:w-auto lg:w-[260px] lg:flex-col lg:items-end lg:mr-8">
              <div className="flex flex-row gap-3 w-full sm:w-auto lg:w-full">
                <button
                  onClick={handleStatusToggle}
                  disabled={actionLoading}
                    className={`flex-1 sm:flex-none inline-flex items-center justify-center px-4 py-2.5 sm:px-6 sm:py-3 rounded-xl font-medium text-xs sm:text-sm transition-all duration-200 whitespace-nowrap ${
                    test.status === 'draft'
                      ? 'bg-emerald-600 hover:bg-emerald-700 text-white'
                      : 'bg-slate-600 hover:bg-slate-700 text-white'
                  } disabled:opacity-50 disabled:cursor-not-allowed`}
                >
                  {actionLoading ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  ) : test.status === 'draft' ? (
                    <FiCheck className="w-4 h-4 mr-2" />
                  ) : (
                    <FiSave className="w-4 h-4 mr-2" />
                  )}
                  <span className="hidden sm:inline">{test.status === 'draft' ? 'Publish' : 'Draft'}</span>
                  <span className="sm:hidden">{test.status === 'draft' ? 'Publish' : 'Draft'}</span>
                </button>

                {!isPublished && (
                  <button
                    onClick={submitHandler}
                    disabled={loading}
                    className={`flex-1 sm:flex-none inline-flex items-center justify-center px-4 py-2.5 sm:px-6 sm:py-3 rounded-xl font-medium text-xs sm:text-sm transition-all duration-200 whitespace-nowrap ${
                      loading
                        ? 'bg-indigo-400 cursor-not-allowed'
                        : 'bg-indigo-600 hover:bg-indigo-700'
                    } text-white disabled:opacity-50`}
                  >
                    {loading ? (
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    ) : (
                      <MdPublish className="w-4 h-4 mr-2" />
                    )}
                    <span className="hidden sm:inline">{loading ? 'Updating...' : 'Update'}</span>
                    <span className="sm:hidden">{loading ? 'Updating...' : 'Update'}</span>
                  </button>
                )}

                {/* More Options Dropdown */}
                <div className="relative" ref={moreDropdownRef}>
                  <button
                    onClick={() => setShowMoreDropdown(!showMoreDropdown)}
                    className="inline-flex items-center justify-center px-3 py-2.5 sm:px-4 sm:py-3 rounded-xl font-medium text-xs sm:text-sm transition-all duration-200 bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300"
                  >
                    <FiMoreVertical className="w-4 h-4" />
                  </button>

                  {showMoreDropdown && (
                    <div className="absolute right-0 mt-2 w-48 bg-white border-2 border-gray-200 rounded-xl shadow-xl z-20 overflow-hidden">
                      <button
                        onClick={() => {
                          handleActiveToggle();
                          setShowMoreDropdown(false);
                        }}
                        disabled={actionLoading}
                        className={`w-full flex items-center gap-3 px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 transition-colors ${
                          actionLoading ? 'opacity-50 cursor-not-allowed' : ''
                        }`}
                      >
                        {actionLoading ? (
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-600"></div>
                        ) : test.is_active ? (
                          <FiPause className="w-4 h-4" />
                        ) : (
                          <FiPlay className="w-4 h-4" />
                        )}
                          <span>{test.is_active ? 'Deactivate Test' : 'Activate Test'}</span>
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Mark Distribution Summary */}
        {test && questions.length > 0 && !isPublished && (
          <MarkDistributionSummary
            totalScore={test.total_score || 0}
            questions={questions}
            scoringMode={test.scoring_mode}
            mcqMaxScore={test.mcq_max_score}
            theoryMaxScore={test.theory_max_score}
          />
        )}

        {/* Questions Section */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-slate-800">
              {isPublished ? 'Questions' : `Edit Questions (${questions.length})`}
            </h2>
            {!isPublished && (
              <button
                onClick={addQuestion}
                className="inline-flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-medium text-sm transition-all duration-200"
              >
                <MdAdd className="w-4 h-4 mr-2" />
                Add Question
              </button>
            )}
          </div>
          
          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl">
              <div className="flex items-center">
                <FiX className="w-5 h-5 text-red-600 mr-2" />
                <span className="text-red-800 font-medium">{error}</span>
              </div>
            </div>
          )}

          {success && (
            <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-xl">
              <div className="flex items-center">
                <FiCheck className="w-5 h-5 text-green-600 mr-2" />
                <span className="text-green-800 font-medium">Questions updated successfully!</span>
              </div>
            </div>
          )}

          {isPublished ? (
            <div className="text-center py-16">
              <div className="mx-auto h-24 w-24 text-amber-300 mb-6 flex items-center justify-center">
                <FiCheck className="w-16 h-16" />
              </div>
              <h3 className="text-lg font-semibold text-slate-700 mb-2">Test Published</h3>
              <p className="text-slate-500 text-sm mb-4">
                This test has been published and is now live. For data integrity, you can no longer view or edit the questions.
              </p>
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 max-w-md mx-auto">
                <div className="flex items-center justify-center">
                  <FiCheck className="w-5 h-5 text-amber-600 mr-2" />
                  <span className="text-amber-800 font-medium text-sm">Questions are protected</span>
                </div>
              </div>
            </div>
          ) : questions.length === 0 ? (
            <div className="text-center py-16">
              <div className="mx-auto h-24 w-24 text-slate-300 mb-6 flex items-center justify-center">
                <FiFileText className="w-16 h-16" />
              </div>
              <h3 className="text-lg font-semibold text-slate-700 mb-2">No questions found</h3>
              <p className="text-slate-500 text-sm">Add your first question to get started</p>
              <button
                onClick={addQuestion}
                className="mt-4 inline-flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-medium text-sm transition-all duration-200"
              >
                <MdAdd className="w-4 h-4 mr-2" />
                Add First Question
              </button>
            </div>
          ) : (
            <>
              <div className="space-y-6">
                {questions.map((q, index) => (
                  <div key={q.id} className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center space-x-3">
                        <div className="w-8 h-8 bg-blue-100 text-blue-700 rounded-lg flex items-center justify-center text-sm font-semibold">
                          {index + 1}
                        </div>
                        <h3 className="text-lg font-semibold text-slate-800">
                          Question {index + 1}
                        </h3>
                        {q.questionId && (
                          <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-blue-100 text-blue-700">
                            Existing
                              </span>
                            )}
                      </div>
                    </div>
                    <FormQuestion 
                      questionData={q} 
                      onUpdate={updateQuestion} 
                      onDelete={(id) => deleteQuestion(id)}
                      onDuplicate={duplicateQuestion}
                      onToggleActive={toggleActiveQuestion}
                      isDeleting={deletingQuestionId === q.id}
                    />
                  </div>
                ))}
              </div>
              
              {/* Update Button at Bottom */}
              <div className="mt-8 flex justify-end">
                <button
                  onClick={submitHandler}
                  disabled={loading}
                  className={`inline-flex items-center justify-center px-8 py-3 rounded-xl font-medium text-sm transition-all duration-200 ${
                    loading
                      ? 'bg-indigo-400 cursor-not-allowed'
                      : 'bg-indigo-600 hover:bg-indigo-700'
                  } text-white disabled:opacity-50 shadow-lg hover:shadow-xl`}
                >
                  {loading ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                      <span>Updating Questions...</span>
                    </>
                  ) : (
                    <>
                      <MdPublish className="w-5 h-5 mr-2" />
                      <span>Update All Questions</span>
                    </>
                  )}
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileImport}
        accept=".xlsx,.xls,.csv"
        style={{ display: 'none' }}
      />
      <QuestionSidebar
        onAddQuestion={addQuestion}
        onNappsImport={test?.category === 'assessment' && !isPublished ? () => setShowNappsModal(true) : undefined}
        onImport={handleImportClick}
        onExport={handleExportClick}
        importDisabled={importLoading || isPublished}
        exportDisabled={isPublished || questions.length === 0}
        nappsImportDisabled={isPublished}
      />

      {showNappsModal && (
        <NappsImportModal
          onClose={() => setShowNappsModal(false)}
          onConfirm={handleNappsImport}
          testId={test._id}
          schoolId={schoolId}
        />
      )}

      <PublishTestErrorModal
        details={publishPointsError}
        questions={questions}
        test={test}
        onClose={() => setPublishPointsError(null)}
      />
    </>
  );
};

export default ViewTestQuestions;
